import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@apollo/client';
import i18next from 'i18next';

import { LIST_COMPANY_COMMENTS_QUERY } from '../../pages/clients/gql';
import CommentItem from './CommentItem';
import CommentDialog from './CommentDialog';
import { Comment } from './types';

interface ClientCommentsSectionProps {
  clientId: number;
  companyId: number;
  clientName: string;
}

/**
 * Section component for displaying and managing client-specific comments.
 * Filters comments by clientId. Pre-fills clientId in dialog when creating new comment.
 */
const ClientCommentsSection: React.FC<ClientCommentsSectionProps> = ({
  clientId,
  companyId,
  clientName,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);

  const {
    data: commentsData,
    loading: commentsLoading,
    error: commentsError,
    refetch: refetchComments
  } = useQuery(LIST_COMPANY_COMMENTS_QUERY, {
    variables: { companyId, clientId },
    fetchPolicy: 'cache-and-network',
  });

  const comments: Comment[] = commentsData?.listCompanyComments || [];

  const handleAddComment = () => {
    setEditingComment(null);
    setOpenDialog(true);
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingComment(null);
  };

  const handleCommentSaved = () => {
    refetchComments();
    handleCloseDialog();
  };

  const handleCommentDeleted = () => {
    refetchComments();
  };

  if (commentsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (commentsError) {
    return (
      <Alert severity="error">
        {i18next.t('publiccompany-comments-error-loading')}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {comments.length === 0
            ? i18next.t('publiccompany-comment-no-comments')
            : comments.length === 1
              ? i18next.t('comments-count-one', { count: comments.length })
              : i18next.t('comments-count-many', { count: comments.length })}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddComment}
        >
          {i18next.t('publiccompany-comment-add')}
        </Button>
      </Box>

      {comments.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={4}
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            backgroundColor: 'grey.50'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {i18next.t('publiccompany-comment-no-comments')}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('created-at')}</TableCell>
                <TableCell>{i18next.t('created-by')}</TableCell>
                <TableCell>{i18next.t('comment-dialog-contact-person')}</TableCell>
                <TableCell>{i18next.t('description')}</TableCell>
                <TableCell>{i18next.t('comment-dialog-contact-type')}</TableCell>
                <TableCell align="center">{i18next.t('comment-relevance')}</TableCell>
                <TableCell align="center">{i18next.t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onEdit={handleEditComment}
                  onDeleted={handleCommentDeleted}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CommentDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSaved={handleCommentSaved}
        companyId={companyId}
        clientId={clientId}
        editingComment={editingComment}
      />
    </Box>
  );
};

export default ClientCommentsSection;
